import React, { useState, createRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import cx from 'classnames';
import icons from '../../../data/icons';
import Consent from '../common/Consent';
import ContactDropdown from './ContactDropdown';
import ClarityService from '../../helpers/clarity';
import Recaptcha from '../../Recaptcha';
import Sentry from '../../../lib/sentry';

const reCaptchaRef = createRef();

const NurturingForm = ({
    t, language, consents,
    topics
}) => {
    const [state, setState] = useState({
        agreement_rodo: false,
        agreement_data: false,
        agreement_marketing: false,
        selected_topic: -1,
        responseType: '',
        errorType: null
    });
    const [sending, setSending] = useState(false);
    const formType = 'LemlockNurturingForm';
    const [error, setError] = useState({
        agreement_rodo: false,
        agreement_data: false,
        selected_topic: false
    });
    let correlationId;
    const sessionId = sessionStorage.getItem('session_id');

    const handleRecaptchaError = () => {
        setState({ ...state, responseType: 'error', errorType: 'recaptcha_error' });
    };

    const handleServerError = () => {
        setState({ ...state, responseType: 'error', errorType: 'server_error' });
    };

    const submitNurturingForm = async (e) => {
        e.preventDefault();

        let params = {};
        if (window.location.search !== '') {
            params = JSON.parse(`{"${
                decodeURI(window.location.search.substring(1))
                    .replace(/"/g, '\\"')
                    .replace(/&/g, '","')
                    .replace(/=/g, '":"')
            }"}`);
        }

        error.agreement_data = !state.agreement_data;
        error.agreement_rodo = !state.agreement_rodo;
        error.selected_topic = state.selected_topic === -1;

        const errorAny = Object.keys(error).reduce(
            (acc, key) => error[key] || acc,
            false
        );

        let token = '';
        if (!errorAny && !sending) {
            token = await reCaptchaRef.current.executeAsync();
        }

        if (!errorAny && !sending && token !== '') {
            setSending(true);

            axios({
                method: 'post',
                url: process.env.OPPORTUNITY,
                data: {
                    type: formType,
                    agreement_data: state.agreement_data,
                    agreement_marketing: state.agreement_marketing,
                    agreement_rodo: state.agreement_rodo,
                    language,
                    recaptcha: token,
                    referrer: document.referrer,
                    visit_data: JSON.stringify(params),
                    session_id: sessionId,
                    clarity_project_id: ClarityService.getProjectId()
                },
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })
                .then((response) => {
                    ClarityService.setCorrelationId(response);
                    ClarityService.setCustomAttributes(window.location.pathname);
                    if (response.data && response.data.success) {
                        setState({
                            ...state,
                            agreement_data: false,
                            agreement_marketing: false,
                            agreement_rodo: false,
                            responseType: 'success',
                            errorType: null
                        });
                    } else {
                        setError({
                            agreement_data: !!response.data.error_agreement_data,
                            agreement_marketing: !!response.data.error_agreement_marketing,
                            agreement_rodo: !!response.data.error_agreement_rodo
                        });
                        setState({
                            ...state,
                            responseType: 'error',
                            errorType: 'general_error'
                        });
                    }
                })
                .catch((err) => {
                    Sentry.ex(err);
                    const { response } = err;
                    if (response.data.error === 'RECAPTCHA_LOW_SCORE' || 'RECAPTCHA_VALIDATION_ERROR') {
                        handleRecaptchaError();
                    } else {
                        handleServerError();
                    }
                    axios({
                        method: 'post',
                        url: process.env.OPPORTUNITY,
                        data: {
                            form_info: JSON.stringify({
                                type: formType,
                                agreement_data: state.agreement_data,
                                agreement_marketing: state.agreement_marketing,
                                agreement_rodo: state.agreement_rodo,
                                language,
                                recaptcha: token,
                                referrer: document.referrer,
                                visit_data: JSON.stringify(params),
                                session_id: sessionId
                            })
                        },
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest'
                        }
                    });
                })
                .finally(() => {
                    if (reCaptchaRef.current) reCaptchaRef.current.reset();
                    setSending(false);
                });
        } else {
            setState({ ...state, responseType: 'error', errorType: 'general_error' });
        }
    };

    return (
        state.responseType !== 'success' ?
            <form>
                <ContactDropdown
                  error={ error.selected_topic }
                  topic={ state.selected_topic }
                  setSelectedTopic={ (value) => setState({ ...state, selected_topic: value }) }
                  topics={ topics }
                  t={ t }
                />
                <div>
                    <div className="form-checkbox" id="nurturing-form-checkbox-01">
                        <input
                          type="checkbox"
                          name="agreement_data"
                          id="nurturing-form-checkbox-agree-data"
                          checked={ state.agreement_data }
                          onChange={ () => setState({ ...state, agreement_data: !state.agreement_data }) }
                        />
                        <label htmlFor="nurturing-form-checkbox-agree-data" className={ cx({ error: error.agreement_data }) }/>
                        <Consent t={ t } required content={ consents.contact.text } title={ consents.contact.label }/>
                    </div>
                    <div className="form-checkbox" id="nurturing-form-checkbox-02">
                        <input
                          type="checkbox"
                          name="agreement_marketing"
                          id="nurturing-form-checkbox-agree-marketing"
                          checked={ state.marketing_data }
                          onChange={ () => setState({ ...state, agreement_marketing: !state.agreement_marketing }) }
                        />
                        <label htmlFor="nurturing-form-checkbox-agree-marketing" className={ cx({ error: error.agreement_marketing }) }/>
                        <Consent t={ t } content={ consents.marketing.text } title={ consents.marketing.label }/>
                    </div>
                    <div className="form-checkbox" id="nurturing-form-checkbox-03">
                        <input
                          type="checkbox"
                          name="agreement_rodo"
                          id="nurturing-form-checkbox-agree-rodo"
                          checked={ state.agreement_rodo }
                          onChange={ () => setState({ ...state, agreement_rodo: !state.agreement_rodo }) }
                        />
                        <label htmlFor="nurturing-form-checkbox-agree-rodo" className={ cx({ error: error.agreement_rodo }) }/>
                        <Consent t={ t } required content={ consents.rodo.text } title={ consents.rodo.label }/>
                    </div>
                </div>
                {
                state.errorType === 'recaptcha_error' ?
                    <div className="form-button form-button-gray" id="contact-form-button-refresh-container">
                        <button id="contact-form-button-refresh" onClick={ () => window.location.reload() }>{ t('contact.form.label.refresh') }</button>
                    </div> :
                    state.responseType !== 'success' &&
                    <div className="form-button form-button-gray">
                        <button type="submit" onClick={ submitNurturingForm } disabled={ sending }>
                            {t('contact.form.label.showEmail')}
                        </button>
                    </div>
            }
                {
                    state.responseType && (state.responseType === 'error' &&
                        <div className={ cx('form-message', 'error') } id={ cx('form-message', 'error') }>
                            <span>{ t(`form.message.${ state.errorType }`) }</span>
                        </div>
                    )
            }
                <Recaptcha ref={ reCaptchaRef } language={ language }/>

            </form>
            :
            <div className="nurturing-form-info-box-text">
                <div className="icon-circle">
                    <span
                      className="icon"
                      dangerouslySetInnerHTML={ { __html: icons.social.email } }
                    />
                </div>
                <p
                  dangerouslySetInnerHTML={ { __html: t('contact.form.label.mailToDisclaimerText') } }
                />
                <a
                  style={ { overflowWrap: 'break-word' } }
                  href={ `mailto:${ topics[state.selected_topic].key }+${ correlationId || '_default'
                    }@lemlock.pl?subject=Lemlock ${ topics[state.selected_topic].name } ${ t('contact.form.label.mailToTitle') }` }
                >
                    {topics[state.selected_topic].key}+{correlationId || `_${ sessionId }` || '_default'}@lemlock.pl
                </a>
            </div>
    );
};

NurturingForm.propTypes = {
    t: PropTypes.func.isRequired,
    language: PropTypes.string.isRequired,
    consents: PropTypes.objectOf(Object).isRequired,
    topics: PropTypes.arrayOf(Object).isRequired
};

export default NurturingForm;



// WEBPACK FOOTER //
// ./src/components/content/contact/NurturingForm.jsx